import { camelCase } from 'lodash'
import * as Yup from 'yup'
import { Form, InputGroup, ListGroup, Row, Col, Button, Modal, Alert } from 'react-bootstrap'
import type { AlertState } from '../../common'

type RowValues = {
  id: number
  name: string
}

type UpsertValues = {
  name: string
}

type DeleteItem = {
  id: number
  pageMarginSettingType: string
  name: string
}

type EnumValues = {
  pageMarginSettingTypes: Record<string, string>
}

const _PageMarginOtherSettingForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleSubmit,
  isSettingWritable,
  selectedIds,
  dispatch,
  selectId,
  unselectId,
  rowIndex
}: {
  values: RowValues | null
  errors: Partial<Record<keyof RowValues, string>>
  touched: Partial<Record<keyof RowValues, boolean>>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleSubmit: (values: RowValues) => void
  isSettingWritable: boolean
  selectedIds: number[]
  dispatch: (action: unknown) => void
  selectId: (id: number) => unknown
  unselectId: (id: number) => unknown
  rowIndex: number
}) => {
  if (!values) {
    return <></>
  }
  return (
    <Row sm='auto' className={`row-${rowIndex + 1}`}>
      <Col>
        <InputGroup size='sm' hasValidation>
          <Form.Control
            id={`nameInput-${values.id}`}
            type='text'
            name='name'
            value={values.name || ''}
            disabled={!isSettingWritable}
            onChange={handleChange}
            onBlur={(_) => handleSubmit(values)}
            isInvalid={!!(touched.name && errors.name)}
          />
          <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
        </InputGroup>
      </Col>
      <Col sm='auto'>
        <Form.Check.Input
          type='checkbox'
          name='checkedGroups'
          value={values.id}
          checked={selectedIds?.includes(values.id)}
          onChange={(e) => dispatch((e.target.checked ? selectId : unselectId)(parseInt(e.target.value)))}
        />
      </Col>
    </Row>
  )
}

export const PageMarginOtherSettingForm = Object.assign(_PageMarginOtherSettingForm, {
  schema: Yup.object().shape({
    name: Yup.string().trim().required()
  })
})

const _PageMarginOtherSettingUpsertForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isValid,
  fetching,
  title,
  onClickCancel,
  submitButtonText,
  alert
}: {
  values: UpsertValues
  errors: Partial<Record<keyof UpsertValues, string>>
  touched: Partial<Record<keyof UpsertValues, boolean>>
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  isValid: boolean
  fetching: boolean
  title: string
  onClickCancel: () => void
  submitButtonText: string
  alert: AlertState | null
}) => {
  return (
    <>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      {alert?.show && alert?.alertBody && (
        <Alert className='upsert-char-style-setting-errors-alert' variant='danger'>
          {alert.alertBody}
        </Alert>
      )}
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column className='col-4'>
              欄外項目・その他の項目名称
            </Form.Label>
            <Col>
              <Form.Control
                type='text'
                name='name'
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={!!(touched.name && errors.name)}
              />
              <Form.Control.Feedback type='invalid'>{errors.name}</Form.Control.Feedback>
            </Col>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit' disabled={!isValid || fetching}>
            {submitButtonText}
          </Button>
        </Modal.Footer>
      </Form>
    </>
  )
}

export const PageMarginOtherSettingUpsertForm = Object.assign(_PageMarginOtherSettingUpsertForm, {
  schema: Yup.object().shape({
    order: Yup.number().required().min(0),
    name: Yup.string().trim().required()
  })
})

export const PageMarginOtherSettingDeleteForm = ({
  handleSubmit,
  title,
  submitButtonText,
  onClickCancel,
  selectedIds,
  pageMarginSettings,
  enumValues
}: {
  handleSubmit: React.SubmitEventHandler<HTMLFormElement>
  title: string
  submitButtonText: string
  onClickCancel: () => void
  selectedIds: number[]
  pageMarginSettings: DeleteItem[] | undefined
  enumValues: EnumValues
}) => {
  const selectedIdSet = new Set(selectedIds)
  const deleteStyles = pageMarginSettings?.filter((s) => selectedIdSet.has(s.id))
  return (
    <>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          <p>以下の欄外項目を削除します。よろしいですか？</p>
          <ListGroup>
            {deleteStyles?.map((style) => (
              <ListGroup.Item key={style.id}>
                id: {style.id}, 種別: {enumValues.pageMarginSettingTypes[camelCase(style.pageMarginSettingType)]}, 名称:{' '}
                {style.name}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onClickCancel}>
            キャンセル
          </Button>
          <Button type='submit'>{submitButtonText}</Button>
        </Modal.Footer>
      </Form>
    </>
  )
}
